import { useRef, useState, useEffect, useCallback } from 'react';
import {
    GRID_COLS, GRID_ROWS, CELL_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT,
    SPEEDS, SpeedKey, COLORS,
    INITIAL_SNAKE, INITIAL_DIRECTION,
    HIGH_SCORE_KEY, SPEED_KEY,
} from './snakeConstants';

export type GameState = 'idle' | 'running' | 'paused' | 'dead';

interface Point { x: number; y: number }

// ── Canvas helpers ──────────────────────────────────────────────────────────

function roundRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number
) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
}

function spawnFood(snake: Point[]): Point {
    const occupied = new Set(snake.map(p => `${p.x},${p.y}`));
    let food: Point;
    do {
        food = {
            x: Math.floor(Math.random() * GRID_COLS),
            y: Math.floor(Math.random() * GRID_ROWS),
        };
    } while (occupied.has(`${food.x},${food.y}`));
    return food;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useSnakeGame(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
    // ── Reactive UI state ──
    const [score, setScore]           = useState(0);
    const [highScore, setHighScore]   = useState(() => Number(localStorage.getItem(HIGH_SCORE_KEY) || 0));
    const [gameState, setGameState]   = useState<GameState>('idle');
    const [speed, setSpeedState]      = useState<SpeedKey>(
        () => (localStorage.getItem(SPEED_KEY) as SpeedKey) || 'NORMAL'
    );

    // ── Mutable game refs (no re-render needed) ──
    const snakeRef        = useRef<Point[]>(INITIAL_SNAKE.map(p => ({ ...p })));
    const directionRef    = useRef<Point>({ ...INITIAL_DIRECTION });
    const nextDirRef      = useRef<Point>({ ...INITIAL_DIRECTION });
    const foodRef         = useRef<Point>(spawnFood(INITIAL_SNAKE));
    const scoreRef        = useRef(0);
    const gameStateRef    = useRef<GameState>('idle');
    const speedRef        = useRef<SpeedKey>('NORMAL');
    const animIdRef       = useRef<number>(0);
    const lastTickRef     = useRef<number>(0);
    const dprRef          = useRef<number>(1);

    // ── HiDPI canvas setup ──
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        dprRef.current = dpr;
        canvas.width  = CANVAS_WIDTH  * dpr;
        canvas.height = CANVAS_HEIGHT * dpr;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.scale(dpr, dpr);
    }, [canvasRef]);

    // ── Drawing ──────────────────────────────────────────────────────────────

    const draw = useCallback((timestamp: number) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear
        ctx.fillStyle = COLORS.BG;
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        // Grid
        ctx.strokeStyle = COLORS.GRID;
        ctx.lineWidth = 0.5;
        for (let x = 0; x <= GRID_COLS; x++) {
            ctx.beginPath();
            ctx.moveTo(x * CELL_SIZE, 0);
            ctx.lineTo(x * CELL_SIZE, CANVAS_HEIGHT);
            ctx.stroke();
        }
        for (let y = 0; y <= GRID_ROWS; y++) {
            ctx.beginPath();
            ctx.moveTo(0, y * CELL_SIZE);
            ctx.lineTo(CANVAS_WIDTH, y * CELL_SIZE);
            ctx.stroke();
        }

        // Food — pulsing outer ring (no shadowBlur, cheap to render)
        const food = foodRef.current;
        const pulse = (Math.sin(timestamp / 350) + 1) / 2; // 0..1
        const cx = food.x * CELL_SIZE + CELL_SIZE / 2;
        const cy = food.y * CELL_SIZE + CELL_SIZE / 2;
        const innerR = CELL_SIZE / 2 - 3;
        const outerR = innerR + 2 + pulse * 3;

        // Outer ring
        ctx.strokeStyle = COLORS.FOOD_GLOW;
        ctx.lineWidth   = 1.5;
        ctx.globalAlpha = 0.4 + pulse * 0.4;
        ctx.beginPath();
        ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Inner dot
        ctx.fillStyle = COLORS.FOOD;
        ctx.beginPath();
        ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
        ctx.fill();

        // Snake body (tail → index 1)
        const snake = snakeRef.current;
        const pad = 2;
        const r = 5;
        for (let i = snake.length - 1; i >= 1; i--) {
            const seg = snake[i];
            const alpha = 0.55 + (0.45 * (snake.length - i)) / snake.length;
            ctx.fillStyle = COLORS.SNAKE_BODY;
            ctx.globalAlpha = alpha;
            roundRect(ctx,
                seg.x * CELL_SIZE + pad,
                seg.y * CELL_SIZE + pad,
                CELL_SIZE - pad * 2,
                CELL_SIZE - pad * 2,
                r
            );
            ctx.fill();
        }
        ctx.globalAlpha = 1;

        // Snake head
        const head = snake[0];
        ctx.fillStyle = COLORS.SNAKE_HEAD;
        roundRect(ctx,
            head.x * CELL_SIZE + pad,
            head.y * CELL_SIZE + pad,
            CELL_SIZE - pad * 2,
            CELL_SIZE - pad * 2,
            r
        );
        ctx.fill();

        // Eyes — offset by direction
        const dir = directionRef.current;
        const eyeR = 2.5;
        const eyeOffsets = dir.x === 1  ? [{ x: 5, y: -4 }, { x: 5, y: 4 }]
                         : dir.x === -1 ? [{ x: -5, y: -4 }, { x: -5, y: 4 }]
                         : dir.y === -1 ? [{ x: -4, y: -5 }, { x: 4, y: -5 }]
                                        : [{ x: -4, y: 5 },  { x: 4, y: 5 }];
        const hx = head.x * CELL_SIZE + CELL_SIZE / 2;
        const hy = head.y * CELL_SIZE + CELL_SIZE / 2;
        ctx.fillStyle = COLORS.BG;
        for (const off of eyeOffsets) {
            ctx.beginPath();
            ctx.arc(hx + off.x, hy + off.y, eyeR, 0, Math.PI * 2);
            ctx.fill();
        }

        // Overlays
        const state = gameStateRef.current;
        if (state === 'idle' || state === 'paused' || state === 'dead') {
            ctx.fillStyle = COLORS.OVERLAY;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            ctx.textAlign = 'center';

            if (state === 'idle') {
                ctx.fillStyle = COLORS.TEXT_ACCENT;
                ctx.font = `bold 28px 'Inter', sans-serif`;
                ctx.fillText('🐍 Snake', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 20);
                ctx.fillStyle = COLORS.TEXT_PRIMARY;
                ctx.font = `15px 'Inter', sans-serif`;
                ctx.fillText('Press Enter or click Start', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 16);
            }

            if (state === 'paused') {
                ctx.fillStyle = COLORS.TEXT_ACCENT;
                ctx.font = `bold 26px 'Inter', sans-serif`;
                ctx.fillText('PAUSED', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 10);
                ctx.fillStyle = COLORS.TEXT_PRIMARY;
                ctx.font = `14px 'Inter', sans-serif`;
                ctx.fillText('Press Space or Resume to continue', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 20);
            }

            if (state === 'dead') {
                ctx.fillStyle = '#ef4444';
                ctx.font = `bold 30px 'Inter', sans-serif`;
                ctx.fillText('GAME OVER', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 30);
                ctx.fillStyle = COLORS.TEXT_ACCENT;
                ctx.font = `bold 20px 'Inter', sans-serif`;
                ctx.fillText(`Score: ${scoreRef.current}`, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 6);
                ctx.fillStyle = COLORS.TEXT_DIM;
                ctx.font = `13px 'Inter', sans-serif`;
                ctx.fillText('Press Enter or Play Again', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 32);
            }
        }
    }, [canvasRef]);

    // ── Game loop ─────────────────────────────────────────────────────────────

    const loop = useCallback((timestamp: number) => {
        const interval = SPEEDS[speedRef.current].ms;
        if (timestamp - lastTickRef.current >= interval) {
            lastTickRef.current = timestamp;
            tick();
        }
        draw(timestamp);
        animIdRef.current = requestAnimationFrame(loop);
    }, [draw]); // eslint-disable-line react-hooks/exhaustive-deps

    const stopLoop = () => {
        if (animIdRef.current) cancelAnimationFrame(animIdRef.current);
    };

    const startLoop = useCallback(() => {
        stopLoop();
        lastTickRef.current = 0;
        animIdRef.current = requestAnimationFrame(loop);
    }, [loop]);

    // ── Tick (advance one step) ───────────────────────────────────────────────

    function tick() {
        directionRef.current = { ...nextDirRef.current };
        const snake = snakeRef.current;
        const head = snake[0];
        const dir  = directionRef.current;
        const newHead = { x: head.x + dir.x, y: head.y + dir.y };

        // Wall collision
        if (newHead.x < 0 || newHead.x >= GRID_COLS || newHead.y < 0 || newHead.y >= GRID_ROWS) {
            die();
            return;
        }

        // Self collision (skip the tail tip which will be removed)
        for (let i = 0; i < snake.length - 1; i++) {
            if (snake[i].x === newHead.x && snake[i].y === newHead.y) {
                die();
                return;
            }
        }

        // Eat food
        if (newHead.x === foodRef.current.x && newHead.y === foodRef.current.y) {
            snakeRef.current = [newHead, ...snake];          // grow — no pop
            scoreRef.current += 1;
            setScore(scoreRef.current);
            foodRef.current = spawnFood(snakeRef.current);
        } else {
            snakeRef.current = [newHead, ...snake.slice(0, -1)];
        }
    }

    function die() {
        stopLoop();
        gameStateRef.current = 'dead';
        setGameState('dead');

        const hs = Number(localStorage.getItem(HIGH_SCORE_KEY) || 0);
        if (scoreRef.current > hs) {
            localStorage.setItem(HIGH_SCORE_KEY, String(scoreRef.current));
            setHighScore(scoreRef.current);
        }

        // Draw the dead frame immediately
        requestAnimationFrame((ts) => draw(ts));
    }

    // ── Keyboard controls ─────────────────────────────────────────────────────

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const state = gameStateRef.current;

        if (e.key === 'Enter') {
            if (state === 'idle' || state === 'dead') { startGame(); return; }
        }
        if (e.key === ' ') {
            e.preventDefault();
            if (state === 'running') { pauseGame(); return; }
            if (state === 'paused')  { resumeGame(); return; }
        }

        if (state !== 'running') return;

        const dir = directionRef.current;
        const moves: Record<string, Point> = {
            ArrowUp:    { x: 0,  y: -1 },
            ArrowDown:  { x: 0,  y:  1 },
            ArrowLeft:  { x: -1, y:  0 },
            ArrowRight: { x: 1,  y:  0 },
            w: { x: 0,  y: -1 },
            s: { x: 0,  y:  1 },
            a: { x: -1, y:  0 },
            d: { x: 1,  y:  0 },
        };

        const next = moves[e.key];
        if (!next) return;

        // Prevent 180° reversal
        if (next.x === -dir.x && next.y === -dir.y) return;

        if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key)) {
            e.preventDefault();
        }
        nextDirRef.current = next;

        // On the initial key press (not keyboard repeat), reset the tick
        // timer to 0 so the rAF loop fires a tick on the very next frame (~16ms).
        // Repeat events are ignored so holding a key doesn't exceed game speed.
        if (!e.repeat) {
            lastTickRef.current = 0;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Public actions ────────────────────────────────────────────────────────

    const startGame = useCallback(() => {
        snakeRef.current     = INITIAL_SNAKE.map(p => ({ ...p }));
        directionRef.current = { ...INITIAL_DIRECTION };
        nextDirRef.current   = { ...INITIAL_DIRECTION };
        foodRef.current      = spawnFood(INITIAL_SNAKE);
        scoreRef.current     = 0;
        setScore(0);
        gameStateRef.current = 'running';
        setGameState('running');
        startLoop();
    }, [startLoop]);

    const pauseGame = useCallback(() => {
        stopLoop();
        gameStateRef.current = 'paused';
        setGameState('paused');
        requestAnimationFrame((ts) => draw(ts));
    }, [draw]);

    const resumeGame = useCallback(() => {
        gameStateRef.current = 'running';
        setGameState('running');
        startLoop();
    }, [startLoop]);

    const restartGame = useCallback(() => {
        stopLoop();
        startGame();
    }, [startGame]);

    const setSpeed = useCallback((s: SpeedKey) => {
        speedRef.current = s;
        setSpeedState(s);
        localStorage.setItem(SPEED_KEY, s);
    }, []);

    // ── Keyboard listener lifecycle ───────────────────────────────────────────

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // ── Cleanup on unmount ────────────────────────────────────────────────────

    useEffect(() => {
        // Kick off idle draw
        animIdRef.current = requestAnimationFrame((ts) => draw(ts));
        return () => stopLoop();
    }, [draw]);

    // Sync speedRef with saved preference
    useEffect(() => {
        speedRef.current = speed;
    }, [speed]);

    return {
        score,
        highScore,
        gameState,
        speed,
        startGame,
        pauseGame,
        resumeGame,
        restartGame,
        setSpeed,
    };
}
